import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from './WorkspaceContext';

export interface OperationalContextSnapshot {
  activeFlowId: string | null;
  activeExecutionId: string | null;
  activeStageId: string | null;
  userWip: number;
  globalSla: number;
  bottleneckStage: string | null;
  throughput7d: number;
}

interface OperationalContextValue extends OperationalContextSnapshot {
  activeFlowName: string | null;
  activeExecutionCode: string | null;
  activeStageName: string | null;
  setActiveFlow: (flowId: string | null, flowName?: string | null) => void;
  setActiveExecution: (
    executionId: string | null,
    executionCode?: string | null,
    flowId?: string | null,
    flowName?: string | null,
  ) => void;
  setActiveStage: (stageId: string | null, stageName?: string | null) => void;
  setOperationalMetrics: (
    input: Partial<Pick<OperationalContextSnapshot, 'userWip' | 'globalSla' | 'bottleneckStage' | 'throughput7d'>>,
  ) => void;
  clearOperationalContext: () => void;
}

const STORAGE_KEY_PREFIX = 'workflow.operational.context.v1';

const DEFAULT_SNAPSHOT: OperationalContextSnapshot = {
  activeFlowId: null,
  activeExecutionId: null,
  activeStageId: null,
  userWip: 0,
  globalSla: 0,
  bottleneckStage: null,
  throughput7d: 0,
};

const OperationalContext = createContext<OperationalContextValue>({
  ...DEFAULT_SNAPSHOT,
  activeFlowName: null,
  activeExecutionCode: null,
  activeStageName: null,
  setActiveFlow: () => {},
  setActiveExecution: () => {},
  setActiveStage: () => {},
  setOperationalMetrics: () => {},
  clearOperationalContext: () => {},
});

function buildStorageKey(userId?: string, organizationId?: string): string {
  const userSegment = userId ?? 'anonymous';
  const organizationSegment = organizationId ?? 'global';
  return `${STORAGE_KEY_PREFIX}:${userSegment}:${organizationSegment}`;
}

function loadStoredState(storageKey: string): {
  snapshot: OperationalContextSnapshot;
  activeFlowName: string | null;
  activeExecutionCode: string | null;
  activeStageName: string | null;
} {
  if (typeof window === 'undefined') {
    return {
      snapshot: DEFAULT_SNAPSHOT,
      activeFlowName: null,
      activeExecutionCode: null,
      activeStageName: null,
    };
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return {
        snapshot: DEFAULT_SNAPSHOT,
        activeFlowName: null,
        activeExecutionCode: null,
        activeStageName: null,
      };
    }

    const parsed = JSON.parse(raw) as Partial<OperationalContextSnapshot> & {
      activeFlowName?: string | null;
      activeExecutionCode?: string | null;
      activeStageName?: string | null;
    };

    return {
      snapshot: {
        activeFlowId: parsed.activeFlowId ?? null,
        activeExecutionId: parsed.activeExecutionId ?? null,
        activeStageId: parsed.activeStageId ?? null,
        userWip: Number(parsed.userWip ?? 0),
        globalSla: Number(parsed.globalSla ?? 0),
        bottleneckStage: parsed.bottleneckStage ?? null,
        throughput7d: Number(parsed.throughput7d ?? 0),
      },
      activeFlowName: parsed.activeFlowName ?? null,
      activeExecutionCode: parsed.activeExecutionCode ?? null,
      activeStageName: parsed.activeStageName ?? null,
    };
  } catch {
    return {
      snapshot: DEFAULT_SNAPSHOT,
      activeFlowName: null,
      activeExecutionCode: null,
      activeStageName: null,
    };
  }
}

export const OperationalContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const storageKey = useMemo(
    () => buildStorageKey(user?.id, workspace?.organizationId ?? user?.organizationId ?? user?.organization_id),
    [user?.id, user?.organizationId, user?.organization_id, workspace?.organizationId],
  );
  const initial = useMemo(() => loadStoredState(storageKey), [storageKey]);

  const [snapshot, setSnapshot] = useState<OperationalContextSnapshot>(initial.snapshot);
  const [activeFlowName, setActiveFlowName] = useState<string | null>(initial.activeFlowName);
  const [activeExecutionCode, setActiveExecutionCode] = useState<string | null>(initial.activeExecutionCode);
  const [activeStageName, setActiveStageName] = useState<string | null>(initial.activeStageName);

  useEffect(() => {
    const restored = loadStoredState(storageKey);
    setSnapshot(restored.snapshot);
    setActiveFlowName(restored.activeFlowName);
    setActiveExecutionCode(restored.activeExecutionCode);
    setActiveStageName(restored.activeStageName);
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const payload = {
      ...snapshot,
      activeFlowName,
      activeExecutionCode,
      activeStageName,
    };

    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  }, [storageKey, snapshot, activeFlowName, activeExecutionCode, activeStageName]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flowId = params.get('flowId');
    const flowName = params.get('flowName');
    const executionId = params.get('executionId');
    const executionCode = params.get('executionCode');
    const stageId = params.get('stageId');
    const stageName = params.get('stageName');

    if (!flowId && !executionId && !stageId) return;

    setSnapshot((prev) => {
      const next = {
        ...prev,
        activeFlowId: flowId ?? prev.activeFlowId,
        activeExecutionId: executionId ?? prev.activeExecutionId,
        activeStageId: stageId ?? prev.activeStageId,
      };

      if (
        next.activeFlowId === prev.activeFlowId &&
        next.activeExecutionId === prev.activeExecutionId &&
        next.activeStageId === prev.activeStageId
      ) {
        return prev;
      }

      return next;
    });

    if (flowName) {
      setActiveFlowName((prev) => (prev === flowName ? prev : flowName));
    }
    if (executionCode) {
      setActiveExecutionCode((prev) => (prev === executionCode ? prev : executionCode));
    }
    if (stageName) {
      setActiveStageName((prev) => (prev === stageName ? prev : stageName));
    }
  }, [location.search]);

  const setActiveFlow = useCallback((flowId: string | null, flowName?: string | null) => {
    setSnapshot((prev) => {
      if (prev.activeFlowId === flowId) {
        return prev;
      }

      return { ...prev, activeFlowId: flowId };
    });

    if (flowName !== undefined) {
      setActiveFlowName((prev) => (prev === flowName ? prev : flowName));
    }
  }, []);

  const setActiveExecution = useCallback(
    (
      executionId: string | null,
      executionCode?: string | null,
      flowId?: string | null,
      flowName?: string | null,
    ) => {
      setSnapshot((prev) => {
        const nextFlowId = flowId ?? prev.activeFlowId;

        if (prev.activeExecutionId === executionId && prev.activeFlowId === nextFlowId) {
          return prev;
        }

        return {
          ...prev,
          activeExecutionId: executionId,
          activeFlowId: nextFlowId,
        };
      });

      if (executionCode !== undefined) {
        setActiveExecutionCode((prev) => (prev === executionCode ? prev : executionCode));
      }
      if (flowName !== undefined) {
        setActiveFlowName((prev) => (prev === flowName ? prev : flowName));
      }
    },
    [],
  );

  const setActiveStage = useCallback((stageId: string | null, stageName?: string | null) => {
    setSnapshot((prev) => {
      if (prev.activeStageId === stageId) {
        return prev;
      }

      return { ...prev, activeStageId: stageId };
    });

    if (stageName !== undefined) {
      setActiveStageName((prev) => (prev === stageName ? prev : stageName));
    }
  }, []);

  const setOperationalMetrics = useCallback(
    (
      input: Partial<Pick<OperationalContextSnapshot, 'userWip' | 'globalSla' | 'bottleneckStage' | 'throughput7d'>>,
    ) => {
      setSnapshot((prev) => {
        const next = {
          ...prev,
          userWip: input.userWip ?? prev.userWip,
          globalSla: input.globalSla ?? prev.globalSla,
          bottleneckStage: input.bottleneckStage ?? prev.bottleneckStage,
          throughput7d: input.throughput7d ?? prev.throughput7d,
        };

        if (
          next.userWip === prev.userWip &&
          next.globalSla === prev.globalSla &&
          next.bottleneckStage === prev.bottleneckStage &&
          next.throughput7d === prev.throughput7d
        ) {
          return prev;
        }

        return next;
      });
    },
    [],
  );

  const clearOperationalContext = useCallback(() => {
    setSnapshot((prev) => {
      if (
        prev.activeFlowId === DEFAULT_SNAPSHOT.activeFlowId &&
        prev.activeExecutionId === DEFAULT_SNAPSHOT.activeExecutionId &&
        prev.activeStageId === DEFAULT_SNAPSHOT.activeStageId &&
        prev.userWip === DEFAULT_SNAPSHOT.userWip &&
        prev.globalSla === DEFAULT_SNAPSHOT.globalSla &&
        prev.bottleneckStage === DEFAULT_SNAPSHOT.bottleneckStage &&
        prev.throughput7d === DEFAULT_SNAPSHOT.throughput7d
      ) {
        return prev;
      }

      return DEFAULT_SNAPSHOT;
    });
    setActiveFlowName((prev) => (prev === null ? prev : null));
    setActiveExecutionCode((prev) => (prev === null ? prev : null));
    setActiveStageName((prev) => (prev === null ? prev : null));
  }, []);

  const value = useMemo(
    () => ({
      ...snapshot,
      activeFlowName,
      activeExecutionCode,
      activeStageName,
      setActiveFlow,
      setActiveExecution,
      setActiveStage,
      setOperationalMetrics,
      clearOperationalContext,
    }),
    [
      snapshot,
      activeFlowName,
      activeExecutionCode,
      activeStageName,
      setActiveFlow,
      setActiveExecution,
      setActiveStage,
      setOperationalMetrics,
      clearOperationalContext,
    ],
  );

  return <OperationalContext.Provider value={value}>{children}</OperationalContext.Provider>;
};

export const useOperationalContext = () => useContext(OperationalContext);

export const useIsOperationalRoute = (): boolean => {
  const location = useLocation();
  return /^\/(operacoes|workflow|gestao|minha-mesa|ganhos)\b/.test(location.pathname);
};
